import React, { useState, useEffect } from "react";

import { t } from "i18next";

import BaseActionButton from "../Buttons/BaseActionButton";
import BaseModal from "./BaseModal";
import { validate } from "../../utils/ValidationHelpers";
import couponSchema from "../../schemas/coupon.schema";
import { getDatePlusMonths, shortDateYMD } from "../../utils/DateHelper";
import ValidationError from "../Shared/ValidationError";
import SwitchButton from "../Switches/SwitchButton";

const AddPromoCodeModal = ({
    isVisible,
    setIsVisible,
    addAction,
    couponId,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [error, setError] = useState({});

    const [data, setData] = useState({
        isForever: false,
        quantity: 1,
        expiration: "",
    });

    const { start, end } = getDatePlusMonths(6);

    useEffect(() => {
        validate(
            data,
            couponSchema.IncreaseStockSchema,
            setIsFormValid,
            setError,
            {
                context: {
                    isForever: data.isForever,
                },
            }
        );
    }, [data]);

    const submit = async () => {
        setIsSubmitting(true);
        setIsFormSubmitted(true);

        if (isFormValid) {
            await addAction(getSubmitPayload());
        }

        setIsSubmitting(false);
    };

    const getSubmitPayload = () => {
        return {
            couponId,
            expiresAt: data?.isForever ? null : data?.expiration,
            quantity: data?.quantity,
        };
    };

    const changeQuantity = (isIncrement) => {
        const addedValue = isIncrement ? 1 : -1;
        setData((prevData) => ({
            ...prevData,
            quantity: prevData.quantity + addedValue,
        }));
    };

    const handleOnChange = (e, isNumber) => {
        setData((prevData) => ({
            ...prevData,
            [e.target.name]: isNumber ? Number(e.target.value) : e.target.value,
        }));
    };

    return (
        <>
            <BaseModal
                isVisible={isVisible}
                setIsVisible={setIsVisible}
                title={t("addTitle", { entity: t("promoCodes") })}
                isSmall={true}
                isSubmitting={isSubmitting}
                action={{
                    submitAction: submit,
                    disabled: isFormSubmitted && !isFormValid,
                }}
            >
                <div className="input-group">
                    <label className="uppercase text-xs required">
                        {t("expirationDate")}
                    </label>
                    <div className="flex items-center">
                        <input
                            type="date"
                            name="expiration"
                            onChange={handleOnChange}
                            min={shortDateYMD(start)}
                            max={shortDateYMD(end)}
                            className={`input-element ${
                                data?.isForever ? "disabled" : ""
                            }`}
                        />
                        <SwitchButton
                            label={t("forever")}
                            checked={data.isForever}
                            className="w-2/12 ml-4"
                            onChange={() =>
                                setData((prevData) => ({
                                    ...prevData,
                                    isForever: !data?.isForever,
                                }))
                            }
                        />
                    </div>
                    <ValidationError
                        errorMessage={isFormSubmitted && error["expiration"]}
                    />
                </div>
                <div className="input-group">
                    <label className="uppercase text-xs required">
                        {t("quantityTitle", { entity: t("promotionCodes") })}
                    </label>
                    <div className="flex items-center">
                        <input
                            type="number"
                            name="quantity"
                            value={data?.quantity ?? ""}
                            onChange={(e) => handleOnChange(e, true)}
                            className="input-element"
                            placeholder={t("decimalPlacesPlaceholder")}
                        />
                        <BaseActionButton
                            className="table-btn table-btn-blue mx-1"
                            icon="fas fa-plus"
                            onClick={() => changeQuantity(true)}
                            disabled={data?.quantity >= 10}
                        />
                        <BaseActionButton
                            className="table-btn table-btn-blue"
                            icon="fas fa-minus"
                            onClick={() => changeQuantity(false)}
                            disabled={data?.quantity <= 1}
                        />
                    </div>
                    <ValidationError
                        errorMessage={isFormSubmitted && error["quantity"]}
                    />
                </div>
            </BaseModal>
        </>
    );
};

export default AddPromoCodeModal;
